import React, { useEffect, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { useQuery } from 'react-query';
import * as yup from 'yup';

import {
	getWSPRequestAgreementTemplates,
	postWSPRequests,
	postDLSizePriceRequests,
	getWSPRequestAgreement,
} from '../../../services/digitalCodesService';
import { isEmptyObject } from '../../../utils/dataUtils';
import { getSanitizedHTML } from '../../../utils/stringUtils';
import { createMessageForError, toasterNotify } from '../../../utils/toaster';
import { validateToSchema } from '../../../utils/validationUtils';
import BaseModal from '../../BaseModal/BaseModal';
import Forms from '../../Forms/Forms';
import CIBRequestView from './views/CIBRequestView';
import Loading from '../../Loading/Loading';

interface RequestCIBAgreementModalProps {
	show: boolean;
	closeModal: () => void;
	onSubmitted: () => void;
	defaultValues?: Record<string, any>;
	nsUid?: string;
	productPlatformCode?: string;
	componentGameName?: string;
	retailPrice?: number;
	readOnly?: boolean;
	requestId?: number | undefined;
	componentProductType?: ProductComponentData['product_type'];
	componentContentType?: string;
}

interface FormValues {
	certified_price?: string;
	certified_flag?: boolean;
	user_signature?: string;
	[key: string]: any;
}

const schema = (retailPrice?: number) => {
	return yup.object().shape({
		certified_price: yup
			.string()
			.test('minimumPrice', 'Minimum allowed wholesale price is 0.20 USD', (value) =>
				value ? parseFloat(value) > 0.19 : true,
			)
			.test(
				'below_retail',
				`Wholesale price must be the same or less than the retail price of ${retailPrice}.`,
				(value) => !retailPrice || parseFloat(value ?? '') <= retailPrice,
			)
			.test(
				'validDollar',
				'Valid price is required, no amounts over 999.99 or fractions of cents',
				(value) => /^([1-9]\d?\d?|0)(\.\d{1,2})?$/.test(value ?? ''),
			)
			.test(
				'type',
				'Only include numbers and one period in the price, no currency signs',
				(value) => !isNaN(value as any),
			)
			.required('Price must be provided'),
		certified_flag: yup
			.boolean()
			.default(false)
			.required('Box must be checked')
			.test(
				'must_be_true',
				'Box must be checked',
				(value) => value === null || value === true,
			),
		user_signature: yup.string().trim().required('Signature is required'),
	});
};

const productTypeSchema =
	yup.object().shape({
		requested_product_type: yup
			.string()
			.required('Product Type must be selected to continue')
	});

const priceTypeCodeSchema =
	yup.object().shape({
		requested_price_type_code: yup
			.string()
			.required('Code-in-Box Pricing Method is required')
	});

const RequestCIBAgreementModal: React.FC<RequestCIBAgreementModalProps> = ({
	show,
	closeModal,
	onSubmitted,
	defaultValues = {},
	nsUid = '',
	productPlatformCode = '',
	componentGameName = '',
	retailPrice,
	readOnly = false,
	requestId,
	componentProductType,
	componentContentType,
}) => {
	const [selectFormValues, setSelectFormValues] = useState<Record<string, string>>({});
	const [selectedProductType, setSelectedProductType] = useState<string | undefined>();
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [showAllErrors, setShowAllErrors] = useState<boolean>(false);
	const [lastUpdated, setLastUpdated] = useState<number | null >();
	const [readOnlyMode, setReadOnlyMode] = useState<boolean>(true);

	const productTypeSelectionRequired = !readOnlyMode && !componentProductType && productPlatformCode === 'HAC';
	const workingProductType = componentProductType || (productPlatformCode === 'HAC' ? selectedProductType : productPlatformCode);

	const existingDataQuery = useQuery(
		['getWSPRequestAgreement', requestId],
		() => getWSPRequestAgreement(requestId!),
		{ enabled: !!(show && requestId && readOnlyMode), },
	);

	const templateQuery = useQuery(
		['getWSPRequestAgreementTemplates', workingProductType],
		() => getWSPRequestAgreementTemplates(workingProductType ?? ''),
		{ enabled: !!(show && !readOnlyMode && workingProductType) },
	);
	const isLoading = existingDataQuery.isLoading || templateQuery.isLoading;
	const fault =
		(templateQuery.isError && templateQuery.error) ||
		(existingDataQuery.isError && existingDataQuery.error);
	const wspTemplate: any = readOnlyMode
		? existingDataQuery.isSuccess &&
		  existingDataQuery.data.data.wsp_agreement_template
		: templateQuery.isSuccess && templateQuery.data.data;

	const wspTemplateBody = wspTemplate?.template || wspTemplate?.template_body;

	const getAgreementType = (): 'WHOLESALE' | 'DL_SIZE' | undefined => {
		if (readOnlyMode) {
			return 'WHOLESALE';
		}
		if (!readOnlyMode && workingProductType === 'HAC') {
			return 'WHOLESALE';
		}
		if (
			!readOnlyMode &&
			workingProductType === 'BEE' &&
			selectFormValues.requested_price_type_code === 'WHOLESALE'
		) {
			return 'WHOLESALE';
		}
		if (
			!readOnlyMode &&
			workingProductType === 'BEE' &&
			selectFormValues.requested_price_type_code === 'DL_SIZE'
		) {
			return 'DL_SIZE';
		}
		return undefined;
	};

	useEffect(() => {
		if (show) {
			let initialSelectFormValues = {};
			if (componentContentType === 'AOC') {
				initialSelectFormValues = { ...initialSelectFormValues, requested_price_type_code: 'WHOLESALE' };
			}
			setSelectFormValues(initialSelectFormValues);
			setSelectedProductType(undefined);
			setFormValues({ ...defaultValues });
			setShowAllErrors(false);
			setIsSubmitting(false);
			setLastUpdated(null);
			setReadOnlyMode(readOnly);
		}
	}, [show]);

	const changedFormValue = (values: Record<string, any>) => {
		setFormValues(values);
	};

	const isAoc = componentContentType === 'AOC';

	const productTypeValidationErrors = validateToSchema(productTypeSchema, selectFormValues);
	const priceTypeCodeValidationErrors = validateToSchema(priceTypeCodeSchema, selectFormValues);
	const validationErrors = formValues && validateToSchema(schema(retailPrice), formValues);

	const submitForm = async () => {
		if (isSubmitting || !workingProductType) {
			return;
		}
		if (workingProductType === 'BEE' && !isEmptyObject(priceTypeCodeValidationErrors)) {
			setShowAllErrors(true);
			return;
		}

		if (
			!(
				workingProductType === 'BEE' &&
				selectFormValues.requested_price_type_code === 'DL_SIZE'
			) &&
			!isEmptyObject(validationErrors)
		) {
			setShowAllErrors(true);
			return;
		}

		setIsSubmitting(true);

		const apiCall =
			workingProductType === 'HAC' ||
			selectFormValues.requested_price_type_code === 'WHOLESALE'
				? postWSPRequests(
					nsUid,
					{
						wholesale_price_agreement_template_id: wspTemplate.wholesale_price_agreement_template_id,
						certified_flag: formValues?.certified_flag,
						certified_price: formValues?.certified_price,
						user_signature: formValues?.user_signature,
						requested_product_type: selectedProductType || null,
					},
					() => setLastUpdated(new Date().valueOf()),
				)
				: postDLSizePriceRequests(
					nsUid,
					{
						requested_product_type: selectedProductType || null,
					},
					() => setLastUpdated(new Date().valueOf()),
				);

		apiCall
			.then(() => {
				onSubmitted();
				closeModal();
				toasterNotify(
					`Your Code-in-Box price agreement for "${componentGameName}" has been successfully submitted.`,
					'success',
				);
			})
			.catch((err) => {
				toasterNotify(
					createMessageForError(err, 'submitting your Code-in-Box price agreement'),
					'error',
					err,
				);
			})
			.finally(() => {
				setLastUpdated(null);
				setIsSubmitting(false);
			});
	};

	return (
		<BaseModal
			size="xl"
			show={show}
			onCancel={closeModal}
			isSubmitting={isSubmitting}
			fault={fault}
		>
			<BaseModal.Title>
				{readOnlyMode ? 'View Code-in-Box Price Agreement' : 'Request Code-in-Box Price Agreement'}
			</BaseModal.Title>
			{!readOnlyMode && productTypeSelectionRequired && (
				<Row
					as={'section'}
					className={`justify-content-center${
						selectedProductType ? ' BaseModal__disabled-section' : ''
					}`}
				>
					<Col sm={12} lg={10}>
						<Alert variant="info">
							A Product Type must be selected to request a Code-in-Box Price Agreement
							for this component.
						</Alert>
						<Forms
							values={selectFormValues}
							onChange={(newFormValues) => setSelectFormValues(newFormValues)}
							validationErrors={productTypeValidationErrors}
							showAllErrors={showAllErrors}
						>
							<Forms.RadioList id="requested_product_type">
								<Forms.Heading>Product Type</Forms.Heading>
								<Forms.Option value="HAC">Nintendo Switch (HAC)</Forms.Option>
								<Forms.Option value="BEE">Nintendo Switch 2 (BEE)</Forms.Option>
							</Forms.RadioList>
						</Forms>
					</Col>
				</Row>
			)}
			{!readOnlyMode && workingProductType === 'BEE' && (
				<Row
					as={'section'}
					className={`justify-content-center BaseModal__standout-section${isAoc ? ' BaseModal__disabled-section' : ''}`}
				>
					<Col sm={12} lg={10}>
						<Alert variant="info">
							Select the pricing method for Code-in-Box orders for this item.
						</Alert>
						<Forms
							values={
								isAoc
									? { ...selectFormValues, requested_price_type_code: 'WHOLESALE' }
									: selectFormValues
							}
							onChange={(newFormValues) => {
								if (!isAoc) setSelectFormValues(newFormValues);
							}}
							validationErrors={priceTypeCodeValidationErrors}
							showAllErrors={showAllErrors}
						>
							<Forms.RadioList
								id="requested_price_type_code"
								disabled={isAoc}
							>
								<Forms.Heading>Code-in-Box Pricing Method</Forms.Heading>
								<Forms.Option value="WHOLESALE">Wholesale</Forms.Option>
								<Forms.Option value="DL_SIZE">ROM Size</Forms.Option>
							</Forms.RadioList>
						</Forms>
					</Col>
				</Row>
			)}
			{(readOnlyMode || workingProductType) &&
				(isLoading ? (
					<Loading />
				) : getAgreementType() === 'WHOLESALE' ? (
					<Row className="justify-content-center">
						<Col sm={12} lg={10}>
							<h4
								dangerouslySetInnerHTML={{
									__html: getSanitizedHTML(wspTemplateBody?.Title),
								}}
							/>
							<Forms
								onChange={(id) => changedFormValue(id as FormValues)}
								values={
									(readOnlyMode
										? existingDataQuery.data?.data.wholesale_price_request
										: formValues) || {}
								}
								validationErrors={validationErrors}
								showAllErrors={showAllErrors}
							>
								<CIBRequestView template={wspTemplateBody} readOnly={readOnlyMode} />

								<small
									className="float-right"
									dangerouslySetInnerHTML={{
										__html: getSanitizedHTML(`Legal: ${wspTemplate?.version}`),
									}}
								/>
							</Forms>
						</Col>
					</Row>
					)  : getAgreementType() === 'DL_SIZE' ? (
					<Row className="justify-content-center mt-5">
						<Col sm={12} lg={10} className="text-center">
							<p className="fs-5">
								Pricing will be based on the component's download size.
							</p>
							<p className="text-muted">
								Please select Submit to request the price entry.
							</p>
						</Col>
					</Row>
				) : '')}

			{!readOnlyMode && !(workingProductType) && (
				<BaseModal.Submit
					onClick={() => {
						if (isEmptyObject(productTypeValidationErrors)) {
							setShowAllErrors(false);
							setSelectedProductType(selectFormValues.requested_product_type);
						} else {
							setShowAllErrors(true);
						}
					}}
				>
					Select Product Type
				</BaseModal.Submit>
			)}

			{!readOnlyMode && !componentProductType && selectedProductType && (
				<BaseModal.Submit
					variant="outline-secondary"
					onClick={() => setSelectedProductType(undefined)}
				>
					Reselect Product Type
				</BaseModal.Submit>
			)}
			{!readOnlyMode && (workingProductType) && (
				<BaseModal.Submit onClick={() => submitForm()}>Submit</BaseModal.Submit>
			)}
			{!readOnlyMode && (
				<BaseModal.SubmissionStatus lastUpdated={lastUpdated!}>
					Request submission may take time to complete
				</BaseModal.SubmissionStatus>
			)}
		</BaseModal>
	);
};

export default RequestCIBAgreementModal;
